import { css, Rule } from 'glamor'
import * as React from 'react'
import * as PropTypes from 'prop-types'
import * as keycode_ from 'keycode'
import { FormattedMessage } from 'react-intl'

import { useTheme, withTheme, IWithThemeProps } from '../../theme'

import getStyles from '../text-field/styles'

const keycode = keycode_
const NEW_VAL_CONST = '@@newvalue@@'

const defaultFilter = (options: string[], value: string) =>
  options.filter(option => option.toLowerCase().includes(value && value.toLowerCase()))

const noopFilter = (options: string[]) => options

interface ICompletionOptionProps {
  inputValue?: string
  option: string
  onComplete: (option: string) => void
  selected?: boolean
  highlight?: boolean
  isLabel?: boolean
}

const CompletionOption = ({ selected, inputValue, onComplete, option, highlight, isLabel }: ICompletionOptionProps) => {
  const styles = getStyles(useTheme())
  const lowerOption = option.toLowerCase()
  const lowerInputValue = (inputValue || '').toLowerCase()

  /* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
  return (
    <li
      onMouseDown={() => onComplete(option)}
      onTouchStart={() => onComplete(option)}
      {...css([
        styles.completionItem.base,
        selected && styles.completionItem.selected,
        isLabel && styles.completionItem.isLabel,
      ])}
    >
      {isLabel || !highlight || !inputValue
        ? option
        : [
            <span key={`${option}-pre`}>{option.substring(0, lowerOption.indexOf(lowerInputValue))}</span>,
            <span key={`${option}-center`} {...css(styles.completionItemHighlight.base)}>
              {option.substring(
                lowerOption.indexOf(lowerInputValue),
                lowerOption.indexOf(lowerInputValue) + inputValue.length
              )}
            </span>,
            <span key={`${option}-post`}>
              {option.substring(lowerOption.indexOf(lowerInputValue) + inputValue.length)}
            </span>,
          ]}
    </li>
  )
}

CompletionOption.propTypes = {
  selected: PropTypes.bool,
  inputValue: PropTypes.string,
  onComplete: PropTypes.func,
  option: PropTypes.string,
  highlight: PropTypes.bool,
  isLabel: PropTypes.bool,
}

interface ICompletionListProps {
  options: string[] | { [key: string]: string[] }
  inputValue?: string
  onComplete: (option: string) => void
  onCreate?: (option: string) => void
  selected: string | null
  highlight?: boolean
  innerRef?: React.RefObject<HTMLOListElement>
  openListUpwards: boolean
}

const CompletionListOptions = ({
  options,
  selected,
  onComplete,
  highlight,
  inputValue,
  innerRef,
  openListUpwards,
  onCreate,
}: ICompletionListProps) => {
  const styles = getStyles(useTheme())
  const optionBaseProps = {
    highlight,
    onComplete,
    inputValue,
  }

  // Construct an array of props for filter options
  const filterOptions: {
    selected: boolean
    isLabel: boolean
    option: string
  }[] = Array.isArray(options)
    ? options.map(option => ({
        selected: selected === option,
        isLabel: false,
        option,
      }))
    : Object.entries(options)
        .map(([label, opts]) => [
          {
            selected: false,
            isLabel: true,
            option: label,
          },
          ...opts.map(option => ({
            selected: selected === option,
            isLabel: false,
            option,
          })),
        ])
        .reduce((acc, items) => acc.concat(items), [])

  const offsetToInputField = parseInt(styles.completionList.base.top, 10)
  const itemHeight = parseInt(styles.completionItem.base.height, 10)
  const listMaxHeight = parseInt(styles.completionList.base.maxHeight, 10)

  return (
    <ol
      {...css({
        ...styles.completionList.base,
        ...(openListUpwards
          ? { top: Math.max((filterOptions.length - 1) * -itemHeight - offsetToInputField, -(listMaxHeight + 3)) }
          : {}),
      })}
      ref={innerRef}
    >
      {filterOptions.map(optionProps => (
        <CompletionOption key={optionProps.option} {...optionBaseProps} {...optionProps} />
      ))}
      {onCreate && inputValue && (
        /* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
        <li
          onMouseDown={() => onCreate(inputValue)}
          onTouchStart={() => onCreate(inputValue)}
          {...css([
            styles.completionItem.base,
            styles.completionItem.createable,
            selected === NEW_VAL_CONST && styles.completionItem.selected,
          ])}
        >
          <FormattedMessage
            defaultMessage={`+ Create "{inputValue}"`}
            id="ui-kit.completion-list.createable-label"
            values={{
              inputValue,
            }}
          />
        </li>
      )}
    </ol>
  )
}

CompletionListOptions.propTypes = {
  options: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
  selected: PropTypes.string,
  onComplete: PropTypes.func,
  highlight: PropTypes.bool,
  inputValue: PropTypes.string,
  innerRef: PropTypes.object,
}

export interface ICompletionListWrapperProps {
  disableFiltering?: boolean
  filterFunction?: (options: string[], value: string) => string[]
  highlightOptions?: boolean
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void
  onComplete: (option: string | null) => void
  onCreate?: (option: string) => void
  onFocus?: (event: React.FocusEvent<HTMLInputElement>) => void
  options?: string[] | { [key: string]: string[] }
  readOnly?: boolean
  style?: Rule
  value?: string

  children: (props: {
    onChange: (event: React.ChangeEvent<HTMLInputElement>) => void
    onBlur: (event: React.FocusEvent<HTMLInputElement>) => void
    onFocus: (event: React.FocusEvent<HTMLInputElement>) => void
    onKeyDown: (event: React.KeyboardEvent<HTMLInputElement>) => void
    value?: string
    readOnly: boolean
  }) => React.ReactNode
}

interface ICompletionListWrapperState {
  selected: string | null
  isFocused: boolean
  listOffset: number
}

export const CompletionListWrapper = withTheme(
  class CompletionListWrapper extends React.Component<
    ICompletionListWrapperProps & IWithThemeProps,
    ICompletionListWrapperState
  > {
    static defaultProps = {
      highlightOptions: false,
      disableFiltering: false,
    }

    constructor(props: ICompletionListWrapperProps & IWithThemeProps) {
      super(props)

      this.completionList = React.createRef()
      this.autoCompleteRef = React.createRef()
    }

    state = {
      selected: null,
      isFocused: false,
      listOffset: 0,
    }

    componentDidUpdate() {
      const { listOffset } = this.state

      const styles = getStyles(this.props.theme)

      if (this.completionList.current) {
        this.completionList.current.scrollTop = parseInt(styles.completionItem.base.height, 10) * listOffset
      }
    }

    handleComplete() {
      const options = this.getCompletionOptions()
      const { onCreate, onComplete } = this.props
      const optionList = Array.isArray(options)
        ? options
        : Object.values(options).reduce((acc, items) => acc.concat(items), [])

      if (optionList.length === 0 && !onCreate && !this.props.value) {
        return false
      }

      if (optionList.length === 1) {
        onComplete(optionList[0])
      } else if (onCreate && this.props.value && this.state.selected === NEW_VAL_CONST) {
        onCreate(this.props.value)
      } else if (this.state.selected) {
        onComplete(this.state.selected)
      }

      this.setState({
        selected: null,
        listOffset: 0,
        isFocused: false,
      })

      return true
    }

    handleKeyDown(event: React.KeyboardEvent<HTMLInputElement>) {
      let action

      switch (event.keyCode) {
        case keycode('up'):
          action = () => this.highlightOption('previous')
          break
        case keycode('down'):
          action = () => this.highlightOption('next')
          break
        case keycode('enter'):
        case keycode('tab'):
          action = () => this.handleComplete()
          break
        default:
          null
      }

      if (action && action() && keycode(event.keyCode) !== 'tab') {
        event.preventDefault()
      }
    }

    handleFocus(event: React.FocusEvent<HTMLInputElement>) {
      const { onFocus, readOnly } = this.props

      if (!readOnly) {
        this.setState({ isFocused: true })
      }

      if (onFocus) return onFocus(event)
    }

    handleBlur(event: React.FocusEvent<HTMLInputElement>) {
      const { onBlur } = this.props

      this.setState({ isFocused: false })

      if (onBlur) return onBlur(event)
    }

    getCompletionOptions() {
      const { value, options, filterFunction, disableFiltering } = this.props

      if (!options) {
        return []
      }

      const filterOptions = disableFiltering ? noopFilter : filterFunction || defaultFilter

      if (Array.isArray(options)) {
        return filterOptions(options, value || '')
      }

      const filteredOptions: { [label: string]: string[] } = {}

      Object.entries(options).map(([label, val]) => {
        const filteredOptionList = filterOptions(val, value || '')

        if (filteredOptionList.length > 0) {
          filteredOptions[label] = filteredOptionList
        }
      })

      return filteredOptions
    }

    completionList: React.RefObject<HTMLOListElement>
    autoCompleteRef: React.RefObject<HTMLOListElement>

    static VARIANTS = ['normal', 'success']

    highlightOption(direction: 'previous' | 'next') {
      const completionOptions = this.getCompletionOptions()
      const currentSelected = this.state.selected

      const optionList = Array.isArray(completionOptions)
        ? completionOptions
        : Object.values(completionOptions).reduce((acc, items) => acc.concat(items), [])

      if (optionList.length === 0 && (!this.props.onCreate || !this.props.value)) {
        return false
      }

      const styles = getStyles(this.props.theme)

      let nextSelectedIndex = this.props.onCreate
        ? ((currentSelected
            ? currentSelected === NEW_VAL_CONST
              ? optionList.length
              : optionList.indexOf(currentSelected)
            : -1) +
            (direction === 'previous' ? -1 : 1)) %
          (optionList.length + 1)
        : ((currentSelected ? optionList.indexOf(currentSelected) : -1) + (direction === 'previous' ? -1 : 1)) %
          optionList.length

      if (nextSelectedIndex < 0)
        nextSelectedIndex = this.props.onCreate && this.props.value ? optionList.length : optionList.length - 1

      const itemHeight = parseInt(styles.completionItem.base.height, 10)
      const listMaxHeight = parseInt(styles.completionList.base.maxHeight, 10)

      const nextState = {
        selected: optionList[nextSelectedIndex] ?? NEW_VAL_CONST,
        listOffset: 0,
      }

      if (nextSelectedIndex * itemHeight >= listMaxHeight) {
        nextState.listOffset = nextSelectedIndex + 1 - listMaxHeight / itemHeight
      }

      this.setState(nextState)

      return true
    }

    render() {
      const { readOnly, options, value, onChange, highlightOptions, onComplete, style } = this.props

      const styles = getStyles(this.props.theme)

      const completionOptions = this.getCompletionOptions()
      const hasCompletionOptions =
        this.props.onCreate ||
        (Array.isArray(completionOptions) ? completionOptions : Object.keys(completionOptions)).length > 0

      const yPositionCompletionListWrapper = this.autoCompleteRef.current
        ? this.autoCompleteRef.current.getBoundingClientRect().top
        : 0

      const listMaxHeight = parseInt(styles.completionList.base.maxHeight, 10)

      const openListUpwards = window.innerHeight - (yPositionCompletionListWrapper + listMaxHeight) < 0

      return (
        <span ref={this.autoCompleteRef} {...css(styles.wrapper.base, style)}>
          {this.props.children({
            onKeyDown: event => this.handleKeyDown(event),
            value,
            onBlur: event => this.handleBlur(event),
            onFocus: event => this.handleFocus(event),
            readOnly: !!readOnly,
            onChange,
          })}

          {options && hasCompletionOptions && this.state.isFocused && (
            <CompletionListOptions
              highlight={highlightOptions}
              innerRef={this.completionList}
              inputValue={value}
              onComplete={option => onComplete(option)}
              onCreate={this.props.onCreate}
              openListUpwards={openListUpwards}
              options={completionOptions}
              selected={this.state.selected}
            />
          )}
        </span>
      )
    }
  }
)
